import { CheckIcon, IconProps, InfoIcon, WarningIcon } from "@chakra-ui/icons";
import {
  Alert,
  AlertDescription,
  AlertProps,
  AlertStatus,
  AlertTitle,
  Button,
  ButtonProps,
  CloseButton,
  ComponentWithAs,
  HStack,
  Icon,
  IconButton,
  VStack,
} from "@chakra-ui/react";
import { ReactNode } from "react";
import { IconType } from "react-icons";

import { useIsMobile } from "../../hooks";

export const ALERT_ICON: Record<AlertStatus, { color: string; icon: ComponentWithAs<any, IconProps> } | null> = {
  error: { color: "red.500", icon: WarningIcon },
  info: { color: "blue.500", icon: InfoIcon },
  loading: null,
  success: { color: "green.500", icon: CheckIcon },
  warning: { color: "orange.500", icon: WarningIcon },
};

interface AlertIconProperties {
  icon?: ComponentWithAs<any, IconProps>;
  status: AlertStatus;
}

const AlertIcon = ({
  status,
  icon: Icon = ALERT_ICON[status]?.icon,
}: AlertIconProperties): null | React.ReactElement => {
  if (Icon) {
    return <Icon color={ALERT_ICON[status]?.color} />;
  }

  return null;
};

export interface MessageAlertButtonProperties extends Omit<ButtonProps, "children" | "icon"> {
  children: string;
  icon: IconType;
}

export const MessageAlertButton = ({
  children,
  icon,
  ...properties
}: MessageAlertButtonProperties): React.ReactElement => {
  const isMobile = useIsMobile();

  const commonProperties = {
    bg: "white",
    colorScheme: "gray",
    variant: "outline",
  };

  return isMobile ? (
    <IconButton aria-label={children} icon={<Icon as={icon} />} {...commonProperties} {...properties} />
  ) : (
    <Button leftIcon={<Icon as={icon} />} size="sm" {...commonProperties} {...properties}>
      {children}
    </Button>
  );
};

export interface MessageAlertProperties extends AlertProps {
  description?: ReactNode | string;

  icon?: ComponentWithAs<any, IconProps>;
  onClose?: () => void;
  status?: AlertStatus;
  title?: string;
}

export const MessageAlert = ({
  children,
  description,
  icon,
  onClose,
  status = "success",
  title,
  ...properties
}: MessageAlertProperties): React.ReactElement => (
  <Alert data-testid="toast" onClick={onClose} status={status} variant="left-accent" {...properties}>
    <HStack flex={1} gap={2} w="full">
      <AlertIcon icon={icon} status={status} />
      <VStack align="stretch" flex={1} justifyContent="center">
        {title && <AlertTitle lineHeight="normal">{title}</AlertTitle>}
        {description && <AlertDescription lineHeight="normal">{description}</AlertDescription>}
      </VStack>
      {children}
      {onClose && <CloseButton size="sm" />}
    </HStack>
  </Alert>
);
